import { Select } from "@/components/flexkit/select/Select";
import { StatusFilterValue } from "@/components/settings/organization/members/members-utils";
import { useMembersPage } from "@/queries/organization-queries";
import { UserStatus } from "gitpod-next-api/gitpod/v1/user_pb";
import { useMemo, type FC } from "react";

function toStatusFilterValue(value: string): StatusFilterValue {
    if (isStatusFilterValue(value)) {
        return value;
    }

    return StatusFilterValue.All;
}

function isStatusFilterValue(value: string): value is StatusFilterValue {
    return Object.values(StatusFilterValue).includes(value as StatusFilterValue);
}

const statusFilterOptions = [
    {
        value: StatusFilterValue.All,
        label: "All",
    },
    {
        value: StatusFilterValue.Active,
        label: "Active",
    },
    {
        value: StatusFilterValue.Suspended,
        label: "Suspended",
    },
];

type MemberStatusFilterProps = {
    value: StatusFilterValue;
    onChange: (value: StatusFilterValue) => void;
    showCount?: boolean;
};

const getEnrichedStatusFilterOptionsLabel = (
    option: { value: StatusFilterValue; label: string },
    activeMembers: number,
    suspendedMembers: number,
): string => {
    switch (option.value) {
        case StatusFilterValue.All:
            return `${option.label} (${(activeMembers + suspendedMembers).toLocaleString()})`;
        case StatusFilterValue.Active:
            return `${option.label} (${activeMembers.toLocaleString()})`;
        case StatusFilterValue.Suspended:
            return `${option.label} (${suspendedMembers.toLocaleString()})`;
    }
};

export const MemberStatusFilter: FC<MemberStatusFilterProps> = ({ value, onChange, showCount = true }) => {
    const { data: activeMembers } = useMembersPage({
        filter: {
            statuses: [UserStatus.ACTIVE],
        },
        page: 0,
    });

    const { data: suspendedMembers } = useMembersPage({
        filter: {
            statuses: [UserStatus.SUSPENDED],
        },
        page: 0,
    });

    const enrichedStatusFilterOptions = useMemo(() => {
        return statusFilterOptions.map((option) => {
            if (showCount) {
                return {
                    ...option,
                    label: getEnrichedStatusFilterOptionsLabel(
                        option,
                        activeMembers?.totalCount ?? 0,
                        suspendedMembers?.totalCount ?? 0,
                    ),
                };
            }
            return option;
        });
    }, [activeMembers, suspendedMembers, showCount]);

    return (
        <Select
            data-testid="member-status-filter-trigger"
            value={value}
            onValueChange={(v) => onChange(toStatusFilterValue(v))}
            placeholder="Select status"
            className="grow @lg:min-w-56"
            formatDisplayValue={(_, label) => <span>Status: {label}</span>}
        >
            {enrichedStatusFilterOptions.map((option) => (
                <Select.Item key={option.value} value={option.value}>
                    {option.label}
                </Select.Item>
            ))}
        </Select>
    );
};
